import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { businessInfo } from '../data/mock';

const ContactSection = ({ onOpenForm }) => {
  return (
    <section id="contact" className="py-20 bg-accent/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Get in Touch</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Contact Our Trademark Specialists
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Ready to protect your brand? Our expert team is here to answer your questions and guide you through the trademark registration process.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h3 className="text-2xl font-bold text-primary mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-secondary" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Office Address</h4>
                      <p className="text-gray-600">{businessInfo.address}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-secondary" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Phone Number</h4>
                      <a href={`tel:${businessInfo.phone}`} className="text-secondary hover:underline">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-secondary" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Email Address</h4>
                      <a href={`mailto:${businessInfo.email}`} className="text-secondary hover:underline break-all">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Business Hours */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-secondary" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary mb-1">Business Hours</h4>
                      <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-primary text-white rounded-lg p-6">
                <h4 className="font-bold text-lg mb-4 text-white">Why Choose Us?</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-secondary">1000+</div>
                    <div className="text-xs text-gray-300">Trademarks Filed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-secondary">95%</div>
                    <div className="text-xs text-gray-300">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-secondary">5000+</div>
                    <div className="text-xs text-gray-300">Happy Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-secondary">15+</div>
                    <div className="text-xs text-gray-300">Years Experience</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-primary mb-4">
                  Start Your Trademark Registration
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Take the first step in protecting your brand. Click the button below to fill out our consultation form, and one of our trademark specialists will contact you within 24 hours to discuss your specific needs.
                </p>

                <div className="bg-accent/30 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-primary mb-3">What Happens Next?</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <div className="bg-secondary text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                      <span className="text-sm text-gray-700">Submit your information using our secure form</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="bg-secondary text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                      <span className="text-sm text-gray-700">Receive a free consultation from our trademark expert</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="bg-secondary text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                      <span className="text-sm text-gray-700">Get a comprehensive trademark search report</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="bg-secondary text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">4</div>
                      <span className="text-sm text-gray-700">Begin your trademark registration process</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={onOpenForm}
                  className="w-full bg-secondary text-white px-8 py-4 rounded-md hover:bg-secondary/90 transition-all font-bold shadow-lg text-lg mb-4"
                >
                  Register My Trademark
                </button>

                <p className="text-xs text-gray-500 text-center">
                  No obligation • Free consultation • 100% confidential
                </p>
              </div>

              {/* Map Placeholder */}
              <div className="mt-6 bg-gray-200 rounded-lg overflow-hidden h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3172.3325395304414!2d-121.89384568467422!3d37.33463397984198!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fcca0f2d7cba9%3A0x5d6ba3eef9e31d4a!2s360%20S%20Market%20St%2C%20San%20Jose%2C%20CA%2095113!5e0!3m2!1sen!2sus!4v1635959800000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Office Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
